/* =========================================================
   Quiz picks chosen by the merchandising team

   The "What's bugging you?" quiz picks its products automatically
   (assets/vro-personalization.js, findMatches). For the departments the
   team has sheeted — Hair & Scalp, Health & Energy, Fragrance, Lifestyle,
   Fashion & Styling — they have said exactly which products each
   combination of the four answers shows, so those are shown instead, in
   the sheet's order.

   The data (every combination -> product handles) is in
   assets/vro-quiz-picks-data.js, about 170 KB, so it is fetched the first
   time a quiz is answered rather than on every page. `rows` keys are the
   four answers in `order`, each through keyPart(); values index into
   `products`.
   ========================================================= */
(function () {
  'use strict';

  var DATA_URL = (document.currentScript && document.currentScript.getAttribute('data-picks-url')) || '';
  var dataPromise = null;

  function loadData() {
    if (window.VroQuizPicksData) return Promise.resolve(window.VroQuizPicksData);
    if (dataPromise) return dataPromise;
    if (!DATA_URL) return Promise.resolve(null);
    dataPromise = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = DATA_URL;
      s.async = true;
      s.onload = function () { resolve(window.VroQuizPicksData || null); };
      s.onerror = function () { dataPromise = null; resolve(null); };
      document.head.appendChild(s);
    });
    return dataPromise;
  }

  /* Lower-case letters and digits only, "1K" read as 1000, without a
     trailing "s": so "₹500–₹1K" and "₹500 - ₹1,000" both read "5001000",
     and "All Hair Type" matches "All hair types". Same as the generator. */
  function keyPart(value) {
    return String(value || '').toLowerCase()
      .replace(/(\d+(?:\.\d+)?)k\b/g, function (m, n) { return String(Math.round(parseFloat(n) * 1000)); })
      .replace(/[^a-z0-9]/g, '')
      .replace(/s$/, '');
  }

  /* The row for one problem. Every answer that was given has to match; one
     that was not asked (a skipped step) matches anything. */
  function rowFor(dept, problem, answers) {
    var want = dept.order.map(function (field) {
      return keyPart(field === 'problem' ? problem : answers[field]);
    });
    var exact = dept.rows[want.join('|')];
    if (exact) return exact;
    /* An answer that was not given (Any Budget, a skipped question) matches
       every row. Taking the first one would always show the same corner of the
       sheet (the cheapest band, the first duration), so every matching row is
       merged instead: a product earns points for each row it is in, higher for a
       higher place, and the shelf is the best-scored first. */
    var keys = Object.keys(dept.rows);
    var score = {};
    var first = {};
    var seen = 0;
    var any = false;
    keys.forEach(function (key) {
      var parts = key.split('|');
      for (var j = 0; j < want.length; j++) {
        if (want[j] && parts[j] !== want[j]) return;
      }
      any = true;
      dept.rows[key].forEach(function (idx, pos) {
        score[idx] = (score[idx] || 0) + (5 - Math.min(pos, 4));
        if (!(idx in first)) first[idx] = seen++;
      });
    });
    if (!any) return null;
    var ids = Object.keys(score).map(Number);
    ids.sort(function (x, y) { return score[y] - score[x] || first[x] - first[y]; });
    return ids;
  }

  function deptFor(picks, category) {
    if (picks[category]) return picks[category];
    var want = keyPart(category);
    var found = null;
    Object.keys(picks).forEach(function (name) { if (keyPart(name) === want) found = picks[name]; });
    return found;
  }

  /* Resolves to the handles for these answers, or null when the department
     has no sheet (the quiz then picks automatically, as before). Several
     problems: their lists taken in turn, so each gets its top pick first. */
  function handlesFor(answers) {
    if (!answers || !answers.category) return Promise.resolve(null);
    return loadData().then(function (picks) {
      var dept = picks && deptFor(picks, answers.category);
      if (!dept) return null;
      var problems = (answers.problems && answers.problems.length) ? answers.problems : [''];
      var lists = problems.map(function (p) { return rowFor(dept, p, answers) || []; });
      var out = [];
      var longest = lists.reduce(function (n, l) { return Math.max(n, l.length); }, 0);
      for (var i = 0; i < longest; i++) {
        lists.forEach(function (l) {
          if (i < l.length) {
            var handle = dept.products[l[i]];
            if (handle && out.indexOf(handle) === -1) out.push(handle);
          }
        });
      }
      return out.length ? out : null;
    });
  }

  /* Everything the sheet shows for these problems, across every other
     answer — the "View all" behind a result. Most-picked first (how many
     combinations a product appears in), then the sheet's own order. */
  function allFor(answers) {
    if (!answers || !answers.category) return Promise.resolve(null);
    return loadData().then(function (picks) {
      var dept = picks && deptFor(picks, answers.category);
      if (!dept) return null;
      var pi = dept.order.indexOf('problem');
      var wanted = (answers.problems || []).map(keyPart).filter(Boolean);
      var score = {};
      var first = {};
      var seen = 0;
      Object.keys(dept.rows).forEach(function (key) {
        var part = key.split('|')[pi];
        if (wanted.length && wanted.indexOf(part) === -1) return;
        dept.rows[key].forEach(function (idx, pos) {
          score[idx] = (score[idx] || 0) + (5 - Math.min(pos, 4));
          if (!(idx in first)) first[idx] = seen++;
        });
      });
      var ids = Object.keys(score).map(Number);
      ids.sort(function (a, b) { return score[b] - score[a] || first[a] - first[b]; });
      var out = ids.map(function (i) { return dept.products[i]; }).filter(Boolean);
      return out.length ? out : null;
    });
  }

  /* Each product as the theme's own listing card
     (templates/product.vro-card.liquid), so add-to-cart and wishlist work on
     it exactly as everywhere else. A product that fails to load (deleted,
     unpublished) is skipped rather than failing the row. */
  var cardCache = {};
  function cardHtml(handle) {
    if (!cardCache[handle]) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      cardCache[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '?view=vro-card', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(function (res) {
        if (!res.ok) throw new Error('card ' + res.status);
        return res.text();
      }).catch(function () {
        delete cardCache[handle];
        return '';
      });
    }
    return cardCache[handle];
  }

  function cardsFor(handles) {
    return Promise.all(handles.map(cardHtml)).then(function (htmls) {
      var cards = [];
      htmls.forEach(function (html) {
        if (!html) return;
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var card = holder.querySelector('.feed-card');
        if (card) cards.push(card);
      });
      return cards;
    });
  }

  /* The listing page for a result's "View all": the search page in picks
     mode (sections/main-search.liquid reads vro_picks). q is the problem,
     so the page title reads as what was asked. */
  function viewAllUrl(answers) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var problems = (answers && answers.problems) || [];
    return root + 'search?q=' + encodeURIComponent(problems.join(', ') || answers.category) +
      '&type=product&vro_cat=' + encodeURIComponent(answers.category) +
      '&vro_picks=' + encodeURIComponent(JSON.stringify({ c: answers.category, p: problems }));
  }

  window.VroQuizPicks = { handlesFor: handlesFor, allFor: allFor, cardsFor: cardsFor, viewAllUrl: viewAllUrl, keyPart: keyPart };
})();
